import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import { Button, ClickEvent, DataHeader, DataSourceExecutionEvent, EditRowDecorator, Image, Layout, SaveButton, SlideoutDecorator, Snackbar, Table, TableRow, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutTariffRegionMaster } from "./autogen/AutogenLayoutTariffRegionMaster";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";
import { FilterPanel, SecondaryFilterProps } from "./planning/FilterPanel";
import { TariffRegionExpireRows } from "./TariffRegionExpireRows";
import { TariffRegionLocationSlideOut } from "./TariffRegionLocationSlideOut";
import { TariffRegionUtil } from "./TariffRegionUtil";
import { TariffRegionZipCodeSlideOut } from "./TariffRegionZipCodeSlideOut";
import { ZipCodeGenerator } from "./ZipCodeGenerator";

export class TariffRegionMaster extends AutogenLayoutTariffRegionMaster {

    private layoutZipGenerator = Layout.getLayout("lme/dispatch/ZipCodeGenerator") as ZipCodeGenerator;
    private layoutTariffZipCode = Layout.getLayout("lme/dispatch/TariffRegionZipCodeSlideOut") as TariffRegionZipCodeSlideOut;
    private layoutTariffLocation = Layout.getLayout("lme/dispatch/TariffRegionLocationSlideOut") as TariffRegionLocationSlideOut;
    public zipCodeFilterPanel: FilterPanel;
    public locationFilterPanel: FilterPanel;
    public tariffRegionZipCodeFilters: SecondaryFilterProps[] = TariffRegionUtil.getZipCodesSecondaryFilter();
    public tariffRegionLocationFilters: SecondaryFilterProps[] = TariffRegionUtil.getLocationsSecondaryFilter();
    private setExiprationZipCodesImage: Image;
    private editTariffRegionZipCodeImage: Image;
    private setExiprationLocationsImage: Image;
    private editTariffRegionLocationImage: Image;
    private buttonAddTariffZipCode: Button;
    private buttonAddTariffLocation: Button;

    onLoad(): void | Promise<void> {
        this.validateTariffRegionRecordExist();
        this.addTaskFilter();
        this.setupZipCodeTable();
        this.setupLocationTable();
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
    }

    private validateTariffRegionRecordExist() {
        const viewTariffRegionURL = "lme/dispatch/TariffRegionMaster?mode=update&key=";
        const validationProps: DuplicateIdProps = { tableName:"geographic_region", screenName:"TariffRegionMaster",
            navigateToUrl:viewTariffRegionURL, altFieldNameForDupCheck: "region_name", primaryKeyFieldNameForAlt: "reg_uid",};
        DuplicateId.configureIdTextbox(this.textboxRegionName, validationProps);
    }

    private addTaskFilter() {
        this.zipCodeFilterPanel = new FilterPanel(this.tariffRegionZipCodeFilters, this.tableZipCodes, this.panelZipSecondaryFilters);
        this.locationFilterPanel = new FilterPanel(this.tariffRegionLocationFilters, this.tableLocations, this.panelLocSecondaryFilters);
    }

    private setupZipCodeTable() {
        this.buttonAddTariffZipCode = new Button({
            caption: "Add Zip Code",
            color: "primary",
            imageName: ImageName.add,
            enabled: false,
            onClick: () => this.addTariffZipCodeSlideOut()
        });
        this.editTariffRegionZipCodeImage = new Image({
            name: ImageName.pencil,
            fontSize: '12',
            color: 'default.lightest',
            enabled: false,
            marginRight: 16,
            onClick: () => this.updateTariffZipCodeSlideOut()
        });
        this.setExiprationZipCodesImage = new Image({
            name: ImageName.expireTableRows,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: TariffRegionUtil.defaultImageTooltip(),
            enabled: false,
            marginRight: 16,
            onClick: () => this.setExpirationDateSlideOut(this.tableZipCodes)
        });
        this.tableZipCodes.addCenterTool(this.editTariffRegionZipCodeImage);
        this.tableZipCodes.addCenterTool(this.setExiprationZipCodesImage);
        this.tableZipCodes.addRightTool(this.buttonAddTariffZipCode);
        this.tableZipCodes.addSelectionListener(() => {
            const selectedRowCount = this.tableZipCodes.selectedRowCount;
            this.editTariffRegionZipCodeImage.enabled = selectedRowCount === 1 ? true : false;
            this.editTariffRegionZipCodeImage.color = selectedRowCount === 1 ? 'primary' : 'default.lightest';
            this.editTariffRegionZipCodeImage.tooltip = selectedRowCount === 1 ? 'Update selected Tariff Zip Code' : null;
            this.setExiprationZipCodesImage.enabled = selectedRowCount > 0;
            this.setExiprationZipCodesImage.color = selectedRowCount > 0 ? 'primary' : 'default.lightest';
            this.setExiprationZipCodesImage.tooltip = TariffRegionUtil.getImageTooltip(selectedRowCount);
        });
    }

    addTariffZipCodeSlideOut(): void {
        const layout = this.layoutTariffZipCode;
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        layout.addZipCodeSlideout(regUID, this.sourceTariffRegionZip);
    }

    updateTariffZipCodeSlideOut(): void {
        const layout = this.layoutTariffZipCode;
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        const selectedRow = this.tableZipCodes.selectedRow;
        if (selectedRow == null) {
            Snackbar.showWarningSnackbar("No row selected. Please select a row to proceed.");
            return;
        }
        const modelRow: ModelRow = selectedRow?.data;
        layout.updateZipCodeSlideout(regUID, this.sourceTariffRegionZip, modelRow);
    }

    private setupLocationTable() {
        this.buttonAddTariffLocation = new Button({
            color: "primary",
            caption: 'Add Location',
            imageName: ImageName.add,
            enabled: false,
            onClick: () => this.addTariffLocation()
        });
        this.editTariffRegionLocationImage = new Image({
            name: ImageName.pencil,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: 'Update selected Tariff Location',
            enabled: false,
            marginRight: 16,
            onClick: () => this.updateTariffLocation()
        });
        this.setExiprationLocationsImage = new Image({
            name: ImageName.expireTableRows,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: TariffRegionUtil.defaultImageTooltip(),
            enabled: false,
            marginRight: 16,
            onClick: () => this.setExpirationDateSlideOut(this.tableLocations)
        });
        this.tableLocations.addCenterTool(this.editTariffRegionLocationImage);
        this.tableLocations.addCenterTool(this.setExiprationLocationsImage);
        this.tableLocations.addRightTool(this.buttonAddTariffLocation);
        this.tableLocations.addSelectionListener(() => {
            const selectedRowCount = this.tableLocations.selectedRowCount;
            this.editTariffRegionLocationImage.enabled = selectedRowCount === 1 ? true : false;
            this.editTariffRegionLocationImage.color = selectedRowCount === 1 ? 'primary' : 'default.lightest';
            this.editTariffRegionLocationImage.tooltip = selectedRowCount === 1 ? 'Update selected Tariff Location' : null;
            this.setExiprationLocationsImage.enabled = selectedRowCount > 0;
            this.setExiprationLocationsImage.color = selectedRowCount > 0 ? 'primary' : 'default.lightest';
            this.setExiprationLocationsImage.tooltip = TariffRegionUtil.getImageTooltip(selectedRowCount);
        });
    }

    addTariffLocation(): void {
        const layout = this.layoutTariffLocation;
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        layout.addLocationSlideout(regUID, this.sourceTariffRegionLoc);
    }

    updateTariffLocation(): void {
        const layout = this.layoutTariffLocation;
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        const selectedRow = this.tableLocations.selectedRow;
        if (selectedRow == null) {
            Snackbar.showWarningSnackbar("No row selected. Please select a row to proceed.");
            return;
        }
        const modelRow: ModelRow = selectedRow?.data;
        layout.updateLocationSlideout(regUID, this.sourceTariffRegionLoc, modelRow);
    }

    private setExpirationDateSlideOut(table: Table): void {
        const isTableZipCodes = table === this.tableZipCodes;
        const selectedTableRowData = table.selectedRows.map((tableRow: TableRow) => tableRow.data);
        const layout = Layout.getLayout("lme/dispatch/TariffRegionExpireRows") as TariffRegionExpireRows;
        if (selectedTableRowData.length == 0) {
            Snackbar.showWarningSnackbar("Please select at least one row to apply the expiration.");
            return;
        }
        const prefix = isTableZipCodes ? "Zip Code" : "Location";
        const numberOfRowsSelected = selectedTableRowData.length;
        const title = numberOfRowsSelected === 1  ? `${prefix} - Expiring 1 Record` : `${prefix} - Expiring ${numberOfRowsSelected} Records`;
        new EditRowDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: '40%',
            addlComponents: this.setExpirationDateSaveButton(layout, selectedTableRowData, numberOfRowsSelected, isTableZipCodes),
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            doAfterSlideIn: () => {
                layout.textboxExpireDate.focus();
            },
            onClose: async (_cancelled: boolean) => {
                this.onCloseExpireSlider(layout)
            },
        });
    }

    private setExpirationDateSaveButton(layout: TariffRegionExpireRows, modelRows: ModelRow[], numberOfRowsSelected: number, isTableZipCodes: boolean): Button {
        const button = new Button({
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            caption: "Save",
            color: "primary.reverse",
            rowBreak: false,
            onClick: async () => {
                button.busy = true;
                try {
                    const date = layout.textboxExpireDate.text;
                    if (StringUtil.isEmptyString(date)) {
                        CommonDialogs.showError("Expiration date is required to process and mark the zip codes as expired.");
                        return;
                    }
                    const dialogProps = { yesButtonCaption: "Save & Close", noButtonCaption: "Discard" };
                    const message = `Setting ${numberOfRowsSelected} records Expiration Date to ${date}\n\tWould you like to save the changes?`;
                    const userSelection = await CommonDialogs.showYesNo(message, "Confirm Changes", dialogProps);
                    if (userSelection === true) {
                        const path = `lme/dispatch/tariff-region-${isTableZipCodes ? 'zipcode' : 'location'}-expire`;
                        const expireRowsKey = isTableZipCodes ? 'zip_code_expire_rows' : 'location_expire_rows';
                        const response = await Api.post(path, { "expiration_date": date, [expireRowsKey]: modelRows });
                        const data = response.data[0];

                        if (data?.success) {
                            if (data?.all_rows_skipped) {
                                Snackbar.showWarningSnackbar(data?.message, { millisUntilDismissal: 10000 });
                            } else {
                                Toast.showToast(data?.message, null, { persist: true, millisUntilDismissal: 10000 });
                                await this.delay(5000);
                                layout.slideOut();
                                const image = isTableZipCodes ? this.setExiprationZipCodesImage : this.setExiprationLocationsImage;
                                image.enabled = false;
                                image.color = "default.lightest";
                                image.tooltip = TariffRegionUtil.defaultImageTooltip();
                                const regionUID = this.sourceTariffRegionLtlZipLoc.activeRow.get("reg_uid");
                                this.sourceTariffRegionLtlZipLoc.search({ reg_uid: regionUID });
                            }
                        }
                    }
                } finally {
                    button.busy = false;
                }
            }
        });
        return button;
    }

    private async onCloseExpireSlider(layout: TariffRegionExpireRows) {
        const date = layout.textboxExpireDate.text;
        if (StringUtil.isEmptyString(date)) {
            layout.slideOut();
            return;
        }
        const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
        const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
        const leaveStaySelection = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
        if (leaveStaySelection === true) {
            layout.slideOut();
        }
    }

    private saveButton: SaveButton = SaveButton.create({
        id: "applyButton",
        caption: "Apply",
        rowBreak: false,
        enabled: false,
        onClick: async (event: ClickEvent) => {
            const layout = this.layoutZipGenerator as ZipCodeGenerator;
            layout.saveButton.busy = true;
            layout.buttonClear.enabled = false;
            try {
                const modelRows = layout.modelRows;
                await this.layoutZipGenerator.modelRows.forEach((row) => { row.post()});
                Snackbar.showSnackbar("Applying " + modelRows?.length + " zip code(s)...", { "millisUntilDismissal": 5000 });
                await this.delay(5000);
                layout.slideOut();
                layout.setComponentsEnabled(false);
                layout.resetData();
                this.sourceTariffRegionLtlZipLoc.search({ reg_uid: this.sourceTariffRegionLtlZipLoc.activeRow.get("reg_uid") });
            } catch (error) {
                CommonDialogs.showError("Failed to apply zip codes: " + (error?.message || "Unknown error"));
            } finally {
                layout.saveButton.busy = false;
                layout.buttonClear.enabled = true;
            }
        }
    });

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    imageAddZipCodeRegionOnClick(event: ClickEvent) {
        const layoutZipGenerator = this.layoutZipGenerator;
        new SlideoutDecorator({
            title: "Zip Code Generator",
            layout: layoutZipGenerator,
            fillVerticalSpace: true,
            width: '40%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.saveButton,
            doAfterSlideIn: () => {
                layoutZipGenerator.saveButton = this.saveButton;
                this.saveButton.enabled = false;
                this.saveButton.busy = false;
                layoutZipGenerator.buttonClear.enabled = false;
                const activeRow = this.sourceTariffRegionLtlZipLoc.activeRow;
                layoutZipGenerator.regUid = activeRow?.get("reg_uid");
                layoutZipGenerator.textboxRegionCode.text = activeRow?.get("region_name");
            },
            onClose: async (cancelled: boolean) => {
                if (cancelled && layoutZipGenerator.tableGeneratedZipCodes.data?.length > 0) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                layoutZipGenerator.slideOut();
                layoutZipGenerator.setComponentsEnabled(false);
                layoutZipGenerator.resetData();
            }
        });
    }

    tableZipCodesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.addDblClickListener(() => this.updateTariffZipCodeSlideOut());
    }

    tableLocationsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.addDblClickListener(() => this.updateTariffLocation());
    }

    sourceTariffRegionZipAfterExecution(event: DataSourceExecutionEvent) {
        this.zipCodeFilterPanel.toggleFilterById("active");
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        this.buttonAddTariffZipCode.enabled = this.isTariffRegUIDPresent(regUID) ? true : false;
    }

    sourceTariffRegionLocAfterExecution(event: DataSourceExecutionEvent) {
        this.locationFilterPanel.toggleFilterById("active");
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        this.buttonAddTariffLocation.enabled = this.isTariffRegUIDPresent(regUID) ? true : false;
    }

    sourceTariffRegionLtlZipLocAfterExecution(event: DataSourceExecutionEvent) {
        const regUID = this.mainDataSource.data[0]?.get("reg_uid");
        if (this.isTariffRegUIDPresent(regUID)) {
            this.imageAddZipCodeRegion.enabled = true;
            this.imageAddZipCodeRegion.color = 'primary';
            this.imageAddZipCodeRegion.visible = true;
        }
    }

    private isTariffRegUIDPresent(isTariffUIDPresent: any) {
        return !StringUtil.isEmptyString(isTariffUIDPresent);
    }

}
